import React, { useEffect, useState, useCallback } from "react";
import { Table, Spinner, Button, Form, Row, Col, Badge, Alert as BsAlert } from "react-bootstrap";
import SafetyAbilitazioniService from "../../../services/safety/SafetyAbilitazioniService";

const EMPTY = {
    id_tipo_abil: "",
    titolo: "",
    numero: "",
    ente_rilascio: "",
    data_rilascio: "",
    data_scadenza: "",
    periodicita_mesi: "",
    note: "",
};

function fmtDate(d) {
    if (!d) return "—";
    const s = String(d).substring(0, 10);
    const p = s.split("-");
    if (p.length !== 3) return s;
    return `${p[2]}/${p[1]}/${p[0]}`;
}

/**
 * AbilitazioniPanel
 * Pannello per le abilitazioni/titoli del personale a livello lavoratore
 * (decreto GPG, porto d'armi, idoneita' maneggio armi, tessera, patenti...).
 * Il catalogo dei tipi e' filtrato in base al settore dell'azienda del lavoratore.
 *
 * Props:
 *  - idLavoratore: number (richiesto)
 */
export default function AbilitazioniPanel({ idLavoratore }) {
    const [tipi, setTipi] = useState([]);
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [msg, setMsg] = useState(null);
    const [show, setShow] = useState(false);
    const [form, setForm] = useState(EMPTY);
    const [file, setFile] = useState(null);
    const [saving, setSaving] = useState(false);

    const loadTipi = useCallback(async () => {
        if (!idLavoratore) { setTipi([]); return; }
        try {
            const resp = await SafetyAbilitazioniService.getTipi(idLavoratore);
            const data = resp && typeof resp.json === "function" ? await resp.json() : resp;
            setTipi(Array.isArray(data) ? data : []);
        } catch {
            setTipi([]);
        }
    }, [idLavoratore]);

    const loadItems = useCallback(async () => {
        if (!idLavoratore) { setItems([]); return; }
        setLoading(true);
        setError(null);
        try {
            const resp = await SafetyAbilitazioniService.byLavoratore(idLavoratore);
            const data = resp && typeof resp.json === "function" ? await resp.json() : resp;
            setItems(Array.isArray(data) ? data : []);
        } catch (err) {
            setError(err?.message || "Errore nel caricamento delle abilitazioni.");
        } finally {
            setLoading(false);
        }
    }, [idLavoratore]);

    useEffect(() => { loadTipi(); }, [loadTipi]);
    useEffect(() => { loadItems(); }, [loadItems]);

    const openForm = () => {
        setForm(EMPTY);
        setFile(null);
        setError(null);
        setMsg(null);
        setShow(true);
    };

    const submit = async () => {
        if (!idLavoratore) return;
        if (!form.id_tipo_abil && !form.titolo) {
            setError("Seleziona un tipo o indica un titolo.");
            return;
        }
        setSaving(true);
        setError(null);
        setMsg(null);
        try {
            const fd = new FormData();
            fd.append("id_lavoratore", idLavoratore);
            if (form.id_tipo_abil)     fd.append("id_tipo_abil", form.id_tipo_abil);
            if (form.titolo)           fd.append("titolo", form.titolo);
            if (form.numero)           fd.append("numero", form.numero);
            if (form.ente_rilascio)    fd.append("ente_rilascio", form.ente_rilascio);
            if (form.data_rilascio)    fd.append("data_rilascio", form.data_rilascio);
            if (form.data_scadenza)    fd.append("data_scadenza", form.data_scadenza);
            if (form.periodicita_mesi) fd.append("periodicita_mesi", form.periodicita_mesi);
            if (form.note)             fd.append("note", form.note);
            if (file)                  fd.append("file", file);
            await SafetyAbilitazioniService.create(fd);
            setMsg("Abilitazione aggiunta.");
            setShow(false);
            setForm(EMPTY);
            setFile(null);
            await loadItems();
        } catch (err) {
            setError(err?.message || "Errore nel salvataggio dell'abilitazione.");
        } finally {
            setSaving(false);
        }
    };

    const onDownload = async (a) => {
        setError(null);
        try {
            const fn = `${(a.titolo || a.tipo_denominazione || "abilitazione").toString().replace(/[^\w\-]+/g, "_")}.pdf`;
            await SafetyAbilitazioniService.download(a.id_abilitazione, fn);
        } catch (err) {
            setError(err?.message || "Impossibile scaricare il file (non disponibile).");
        }
    };

    const onDelete = async (a) => {
        if (!a?.id_abilitazione) return;
        if (!window.confirm("Eliminare questa abilitazione?")) return;
        setError(null);
        try {
            await SafetyAbilitazioniService.deleteAbilitazione(a.id_abilitazione);
            await loadItems();
        } catch (err) {
            setError(err?.message || "Errore nell'eliminazione dell'abilitazione.");
        }
    };

    const tipoSel = form.id_tipo_abil ? tipi.find(t => Number(t.id_tipo_abil) === Number(form.id_tipo_abil)) : null;

    if (!idLavoratore) {
        return (
            <BsAlert variant="info" className="py-2 mb-0">
                <i className="bi bi-info-circle me-1" />
                Salva prima il lavoratore per gestire le abilitazioni.
            </BsAlert>
        );
    }

    return (
        <div>
            <div className="d-flex justify-content-between align-items-center mb-2">
                <h6 className="mb-0">
                    <i className="bi bi-patch-check me-2 text-primary" />
                    Abilitazioni e titoli del personale
                </h6>
                <Button size="sm" variant="primary" onClick={openForm} disabled={show}>
                    <i className="bi bi-plus-lg me-1" />
                    Aggiungi abilitazione
                </Button>
            </div>

            <div className="text-muted small mb-2">
                Decreto GPG, porto d'armi, idoneità al maneggio armi, tessera di riconoscimento, patenti/CQC e altri
                titoli con relativa scadenza. Il catalogo proposto dipende dal comparto dell'azienda.
            </div>

            {show && (
                <div className="border rounded p-3 mb-3" style={{ backgroundColor: "#f8f9fa" }}>
                    <div className="fw-semibold mb-2">
                        <i className="bi bi-patch-plus me-1 text-primary" />
                        Nuova abilitazione
                    </div>
                    <Row className="g-2">
                        <Col xs={12} md={6}>
                            <Form.Label className="small mb-0">Tipo abilitazione</Form.Label>
                            <Form.Select
                                size="sm"
                                value={form.id_tipo_abil}
                                onChange={e => setForm(f => ({ ...f, id_tipo_abil: e.target.value }))}
                            >
                                <option value="">— Nessuno / altro —</option>
                                {tipi.map(t => (
                                    <option key={t.id_tipo_abil} value={t.id_tipo_abil}>
                                        {t.denominazione}{t.categoria ? ` (${t.categoria})` : ""}
                                    </option>
                                ))}
                            </Form.Select>
                            <Form.Text className="text-muted">
                                Dal catalogo: precompila nome, ente tipico e periodicità.
                            </Form.Text>
                        </Col>
                        <Col xs={12} md={6}>
                            <Form.Label className="small mb-0">Titolo</Form.Label>
                            <Form.Control
                                size="sm"
                                value={form.titolo}
                                placeholder={tipoSel ? tipoSel.denominazione : "Es. Porto d'armi per difesa personale"}
                                onChange={e => setForm(f => ({ ...f, titolo: e.target.value }))}
                            />
                            <Form.Text className="text-muted">
                                Lascia vuoto per usare il nome del tipo selezionato.
                            </Form.Text>
                        </Col>

                        {tipoSel && (
                            <Col xs={12}>
                                <BsAlert variant="info" className="py-2 mb-1 small">
                                    <div className="fw-semibold">
                                        <i className="bi bi-lightbulb me-1" />
                                        {tipoSel.denominazione}
                                        {tipoSel.riferimento_normativo
                                            ? <span className="text-muted fw-normal"> — {tipoSel.riferimento_normativo}</span>
                                            : null}
                                    </div>
                                    {tipoSel.ente_tipico ? <>Ente di rilascio tipico: <strong>{tipoSel.ente_tipico}</strong>. </> : null}
                                    {tipoSel.periodicita_mesi
                                        ? <>Validità tipica: <strong>{tipoSel.periodicita_mesi} mesi</strong> (la scadenza è calcolata da data di rilascio + periodicità).</>
                                        : <>Senza scadenza predefinita: indica la scadenza se prevista.</>}
                                </BsAlert>
                            </Col>
                        )}

                        <Col xs={6} md={3}>
                            <Form.Label className="small mb-0">Numero / protocollo</Form.Label>
                            <Form.Control
                                size="sm"
                                value={form.numero}
                                onChange={e => setForm(f => ({ ...f, numero: e.target.value }))}
                            />
                            <Form.Text className="text-muted">Numero del decreto/licenza/tessera.</Form.Text>
                        </Col>
                        <Col xs={6} md={3}>
                            <Form.Label className="small mb-0">Ente di rilascio</Form.Label>
                            <Form.Control
                                size="sm"
                                placeholder={tipoSel?.ente_tipico || "Es. Prefettura"}
                                value={form.ente_rilascio}
                                onChange={e => setForm(f => ({ ...f, ente_rilascio: e.target.value }))}
                            />
                        </Col>
                        <Col xs={6} md={3}>
                            <Form.Label className="small mb-0">Data rilascio</Form.Label>
                            <Form.Control
                                size="sm"
                                type="date"
                                value={form.data_rilascio}
                                onChange={e => setForm(f => ({ ...f, data_rilascio: e.target.value }))}
                            />
                        </Col>
                        <Col xs={6} md={3}>
                            <Form.Label className="small mb-0">Periodicità (mesi)</Form.Label>
                            <Form.Control
                                size="sm"
                                type="number"
                                min="0"
                                placeholder="es. 24"
                                value={form.periodicita_mesi}
                                onChange={e => setForm(f => ({ ...f, periodicita_mesi: e.target.value }))}
                            />
                            <Form.Text className="text-muted">Vuoto = nessuna scadenza.</Form.Text>
                        </Col>
                        <Col xs={6} md={3}>
                            <Form.Label className="small mb-0">Scadenza (opz.)</Form.Label>
                            <Form.Control
                                size="sm"
                                type="date"
                                value={form.data_scadenza}
                                onChange={e => setForm(f => ({ ...f, data_scadenza: e.target.value }))}
                            />
                            <Form.Text className="text-muted">Se vuota, da rilascio + periodicità.</Form.Text>
                        </Col>
                        <Col xs={12} md={5}>
                            <Form.Label className="small mb-0">File (PDF / scansione)</Form.Label>
                            <Form.Control
                                size="sm"
                                type="file"
                                accept="application/pdf,image/*"
                                onChange={e => setFile(e.target.files?.[0] || null)}
                            />
                        </Col>
                        <Col xs={12}>
                            <Form.Label className="small mb-0">Note</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={2}
                                size="sm"
                                value={form.note}
                                onChange={e => setForm(f => ({ ...f, note: e.target.value }))}
                            />
                        </Col>
                    </Row>
                    <div className="d-flex justify-content-end gap-2 mt-2">
                        <Button size="sm" variant="secondary" onClick={() => setShow(false)} disabled={saving}>
                            Annulla
                        </Button>
                        <Button size="sm" variant="primary" onClick={submit} disabled={saving}>
                            {saving ? <Spinner size="sm" animation="border" className="me-1" /> : <i className="bi bi-check2 me-1" />}
                            Salva abilitazione
                        </Button>
                    </div>
                </div>
            )}

            {error && <BsAlert variant="danger" className="py-2">{error}</BsAlert>}
            {msg && <BsAlert variant="success" className="py-2">{msg}</BsAlert>}

            {loading ? (
                <div className="text-muted small"><Spinner size="sm" animation="border" className="me-2" />Caricamento abilitazioni…</div>
            ) : items.length === 0 ? (
                <div className="text-muted small">
                    Nessuna abilitazione registrata. Usa <strong>"Aggiungi abilitazione"</strong> per inserire
                    decreto GPG, porto d'armi, tessera di riconoscimento e altri titoli.
                </div>
            ) : (
                <Table size="sm" bordered hover responsive className="mb-0">
                    <thead className="table-light">
                        <tr>
                            <th>Abilitazione</th>
                            <th>Numero</th>
                            <th>Rilascio</th>
                            <th>Scadenza</th>
                            <th>Stato</th>
                            <th className="text-end">Azioni</th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map(a => {
                            const stato = a.stato || "PERMANENTE";
                            const badge = stato === "SCADUTO" ? "danger"
                                : stato === "IN_SCADENZA" ? "warning"
                                : stato === "VALIDO" ? "success" : "light";
                            const badgeText = (stato === "SCADUTO" || stato === "VALIDO") ? "light" : "dark";
                            const label = stato === "IN_SCADENZA" ? "In scadenza"
                                : stato === "PERMANENTE" ? "Senza scadenza"
                                : stato.charAt(0) + stato.slice(1).toLowerCase();
                            return (
                                <tr key={a.id_abilitazione}>
                                    <td>
                                        <div>{a.titolo || a.tipo_denominazione || "—"}</div>
                                        <div className="text-muted small">
                                            {a.tipo_categoria || ""}
                                            {a.ente_rilascio ? `${a.tipo_categoria ? " · " : ""}${a.ente_rilascio}` : ""}
                                        </div>
                                    </td>
                                    <td>{a.numero || <span className="text-muted">—</span>}</td>
                                    <td>{fmtDate(a.data_rilascio)}</td>
                                    <td>{fmtDate(a.data_scadenza)}</td>
                                    <td>
                                        <Badge bg={badge} text={badgeText} className={badge === "light" ? "border" : undefined}>{label}</Badge>
                                        {typeof a.giorni_alla_scadenza === "number" && (stato === "SCADUTO" || stato === "IN_SCADENZA") && (
                                            <span className="text-muted small ms-1">
                                                {a.giorni_alla_scadenza < 0
                                                    ? `${Math.abs(a.giorni_alla_scadenza)} gg fa`
                                                    : `tra ${a.giorni_alla_scadenza} gg`}
                                            </span>
                                        )}
                                    </td>
                                    <td className="text-end">
                                        {a.path_file ? (
                                            <Button size="sm" variant="danger" className="me-1" onClick={() => onDownload(a)} title="Scarica file">
                                                <i className="bi bi-file-earmark-pdf me-1" />
                                                PDF
                                            </Button>
                                        ) : null}
                                        <Button size="sm" variant="outline-danger" onClick={() => onDelete(a)} title="Elimina abilitazione">
                                            <i className="bi bi-trash" />
                                        </Button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </Table>
            )}
        </div>
    );
}
